import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isSmallScreen, placePopover } from '../lib/position';
import { useScrollLock } from '../hooks/useScrollLock';

export interface PopoverProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  /** Title shown on the mobile bottom sheet. */
  title?: string;
  children: ReactNode;
  /** Mobile-only footer (e.g. "Done" for multi-selects). */
  footer?: ReactNode;
  minWidth?: number;
  alignRight?: boolean;
  role?: 'dialog' | 'listbox' | 'menu';
}

/** Anchored popover on desktop, bottom sheet (with backdrop) on phones. Esc / outside click closes. */
export function Popover({ anchorRef, open, onClose, title, children, footer, minWidth = 220, alignRight, role = 'dialog' }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [mobile] = useState(isSmallScreen);
  const [shown, setShown] = useState(false);
  useScrollLock(open && mobile);

  useLayoutEffect(() => {
    if (!open || mobile) return;
    const place = () => {
      const a = anchorRef.current;
      const p = ref.current;
      if (!a || !p) return;
      const r = a.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      p.style.minWidth = `${Math.min(Math.max(r.width, minWidth), vw - 16)}px`;
      p.style.maxHeight = '';
      const pos = placePopover(r, { width: p.offsetWidth, height: p.offsetHeight }, vw, window.innerHeight, alignRight);
      p.style.left = `${pos.left}px`;
      p.style.top = `${pos.top}px`;
      if (pos.maxHeight) p.style.maxHeight = `${pos.maxHeight}px`;
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, mobile, anchorRef, minWidth, alignRight]);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const raf = requestAnimationFrame(() => setShown(true));
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchorRef.current?.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchorRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return createPortal(
    <>
      {mobile && <div className={`ui-backdrop${shown ? ' in' : ''}`} onClick={onClose} />}
      <div ref={ref} className={`${mobile ? 'ui-sheet' : 'ui-pop'}${shown ? ' in' : ''}`} role={role === 'dialog' ? 'dialog' : undefined} aria-label={title}>
        {mobile && (
          <div className="ui-sheet-head">
            <div className="ui-sheet-grip" />
            <div className="ui-sheet-title">{title}</div>
          </div>
        )}
        {children}
        {mobile && footer}
      </div>
    </>,
    document.body,
  );
}
